import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  IconButton, 
  MenuItem 
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getSynthesisConfiguration, 
  createSynthesisConfiguration, 
  updateSynthesisConfiguration 
} from '../../services/synthesisConfigService';
import { 
  SynthesisConfigurationCreate, 
  SynthesisConfigurationUpdate, 
  SynthesisConfigurationResponse 
} from '../../types/synthesisConfig';
import { Delete, Add } from '@mui/icons-material';
import swal from 'sweetalert';
import Select from 'react-select';
import { getAllPublicEntitiesSimple } from '../../services/publicEntityService';
import { PublicEntitySimpleResponse } from '../../types/publicEntity';

interface CouncilMemberForm {
  full_name: string;
  nickname: string;
  type: 'vereador(a)' | 'vereador(a) vice-presidente' | 'vereador(a) presidente';
}

const councilMemberTypes = [
  { value: 'vereador(a)', label: 'Vereador(a)' },
  { value: 'vereador(a) vice-presidente', label: 'Vereador(a) Vice-Presidente' },
  { value: 'vereador(a) presidente', label: 'Vereador(a) Presidente' },
];

const SynthesisConfigurationForm: React.FC = () => {
  const { cfgId } = useParams<{ cfgId: string }>();
  const isEditMode = Boolean(cfgId);
  const navigate = useNavigate();

  // Estado para a seleção da entidade
  const [publicEntities, setPublicEntities] = useState<PublicEntitySimpleResponse[]>([]);
  const [selectedEntityOption, setSelectedEntityOption] = useState<{ value: string; label: string } | null>(null);

  // Estados do formulário para os demais campos
  const [entityName, setEntityName] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [cep, setCep] = useState<string>('');
  const [councilMembers, setCouncilMembers] = useState<CouncilMemberForm[]>([
    { full_name: '', nickname: '', type: 'vereador(a)' }
  ]);
  const [loading, setLoading] = useState<boolean>(false);

  // Busca as entidades públicas do serviço do Publisol Connect
  const fetchPublicEntities = async () => {
    try {
      const data = await getAllPublicEntitiesSimple();
      setPublicEntities(data);
    } catch (error) {
      console.error('Erro ao buscar entidades públicas:', error);
      swal('Erro', 'Não foi possível buscar as entidades públicas.', 'error');
    }
  };

  // Busca a configuração existente em modo de edição
  const fetchConfig = async () => {
    if (!cfgId) return;
    setLoading(true);
    try {
      const data: SynthesisConfigurationResponse = await getSynthesisConfiguration(cfgId);
      setEntityName(data.entity_name);
      setStreet(data.address.street);
      setCep(data.address.cep);
      if (data.council_members && data.council_members.length > 0) {
        setCouncilMembers(
          data.council_members.map(member => ({
            full_name: member.full_name,
            nickname: member.nickname || '',
            type: member.type,
          }))
        );
      } else {
        setCouncilMembers([]);
      }
    } catch (error) {
      console.error('Erro ao buscar configuração:', error);
      swal('Erro', 'Não foi possível buscar a configuração.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicEntities();
    if (isEditMode && cfgId) {
      fetchConfig();
    }
  }, [isEditMode, cfgId]);

  // Atualiza o estado ao selecionar a entidade no dropdown
  const handleEntityChange = (selected: { value: string; label: string } | null) => {
    setSelectedEntityOption(selected);
    if (selected) {
      // Define o nome da entidade com base na opção selecionada
      setEntityName(selected.label);
    } else {
      setEntityName('');
    }
  };

  const handleAddMember = () => {
    setCouncilMembers([...councilMembers, { full_name: '', nickname: '', type: 'vereador(a)' }]);
  };

  // Função para atualizar um membro do conselho
  const handleMemberChange = <K extends keyof CouncilMemberForm>(
    index: number,
    field: K,
    value: CouncilMemberForm[K]
  ) => {
    const updatedMembers = [...councilMembers];
    updatedMembers[index][field] = value;
    setCouncilMembers(updatedMembers);
  };

  const handleRemoveMember = (index: number) => {
    const updatedMembers = [...councilMembers];
    updatedMembers.splice(index, 1);
    setCouncilMembers(updatedMembers);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entityName || !street || !cep) {
      swal('Erro', 'Preencha todos os campos obrigatórios.', 'error');
      return;
    }
    if (!isEditMode && !selectedEntityOption) {
      swal('Erro', 'Selecione uma entidade para criar a configuração.', 'error');
      return;
    }
    setLoading(true);
    try {
      if (isEditMode && cfgId) {
        const payload: SynthesisConfigurationUpdate = {
          entity_name: entityName,
          address: { street, cep },
          council_members: councilMembers
            .filter(member => member.full_name.trim() !== '')
            .map(member => ({
              full_name: member.full_name,
              nickname: member.nickname,
              type: member.type,
            })),
        };
        await updateSynthesisConfiguration(cfgId, payload);
        swal('Sucesso', 'Configuração atualizada com sucesso.', 'success');
      } else {
        const payload: SynthesisConfigurationCreate = {
          // Usa o ID da entidade selecionada para a criação
          id: selectedEntityOption!.value,
          entity_name: entityName,
          address: { street, cep },
          council_members: councilMembers
            .filter(member => member.full_name.trim() !== '')
            .map(member => ({
              full_name: member.full_name,
              nickname: member.nickname,
              type: member.type,
            })),
        };
        await createSynthesisConfiguration(payload);
        swal('Sucesso', 'Configuração criada com sucesso.', 'success');
      }
      navigate('/synthesis/configurations');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      swal('Erro', 'Não foi possível salvar a configuração.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {isEditMode ? 'Editar Configuração do Synthesis' : 'Nova Configuração do Synthesis'}
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Em modo criação, permite selecionar a entidade via dropdown */}
            {!isEditMode && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Selecione a Entidade
                </Typography>
                <Select
                  placeholder="Escolha a entidade..."
                  options={publicEntities.map((ent) => ({ value: ent.id, label: ent.name }))}
                  value={selectedEntityOption}
                  onChange={handleEntityChange}
                  isClearable
                />
              </Grid>
            )}
            {/* Campo de Nome da Entidade */}
            <Grid item xs={12}>
              <TextField
                label="Nome da Entidade"
                value={entityName}
                onChange={(e) => setEntityName(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Rua"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="CEP"
                value={cep}
                onChange={(e) => setCep(e.target.value)}
                fullWidth
                required
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Membros do Conselho
            </Typography>
            {councilMembers.map((member, index) => (
              <Grid container spacing={2} key={index} sx={{ mt: 1, alignItems: 'center' }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Nome Completo"
                    value={member.full_name}
                    onChange={(e) => handleMemberChange(index, 'full_name', e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Apelido"
                    value={member.nickname}
                    onChange={(e) => handleMemberChange(index, 'nickname', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={10} sm={3}>
                  <TextField
                    select
                    label="Tipo"
                    value={member.type}
                    onChange={(e) =>
                      handleMemberChange(
                        index,
                        'type',
                        e.target.value as 'vereador(a)' | 'vereador(a) vice-presidente' | 'vereador(a) presidente'
                      )
                    }
                    fullWidth
                  >
                    {councilMemberTypes.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={2} sm={1}>
                  <IconButton 
                    onClick={() => handleRemoveMember(index)}
                    disabled={councilMembers.length === 1}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button variant="outlined" startIcon={<Add />} onClick={handleAddMember} sx={{ mt: 2 }}>
              Adicionar Membro
            </Button>
          </Box>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate('/synthesis/configurations')} sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default SynthesisConfigurationForm;